// =====================================================================================
// INVRT Radar - web console - HIGH-INTENT seam hooks (Story N3)
//
// The four canonical reads of the /v1/radar/* proxy seam, on the app's own api base, with
// query keys keyed by the selected organisation so an org switch refetches rather than
// rendering another tenant's pool for a frame. There is deliberately NO trigger hook: the
// canonical route table carries reads only, runs are started by the HIGH-INTENT worker,
// and inventing a POST here would call a route the seam does not expose.
// =====================================================================================

import { useMemo } from "react";
import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import {
  parseCampaignsPage,
  parseLedgerPage,
  parseLeadsPage,
  parseRunsPage,
  parseStats,
  type RadarCampaignsPage,
  type RadarLedgerPage,
  type RadarLeadsPage,
  type RadarRunsPage,
  type RadarStats,
} from "@/lib/types.radar";

/** The seam's bounded page size for the search route, stated next to the results. */
export const LEADS_PAGE_LIMIT = 25;

/** The engine's default delivery-ledger page bound. */
export const LEDGER_PAGE_LIMIT = 50;

/** The pool-wide binding-law filters, exactly as the canonical route documents them. */
export interface RadarLeadFilters {
  state: string;
  role: string;
  industry: string;
  tag: string;
  intentBand: string;
  campaignId: string;
}

export const EMPTY_LEAD_FILTERS: RadarLeadFilters = {
  state: "",
  role: "",
  industry: "",
  tag: "",
  intentBand: "",
  campaignId: "",
};

/** True when any binding filter is set - the difference between a filtered miss and an empty pool. */
export function hasLeadFilters(filters: RadarLeadFilters): boolean {
  return (
    filters.state.trim() !== "" ||
    filters.role.trim() !== "" ||
    filters.industry.trim() !== "" ||
    filters.tag.trim() !== "" ||
    filters.intentBand !== "" ||
    filters.campaignId !== ""
  );
}

export function toRadarSearchQuery(filters: RadarLeadFilters, cursor?: string): Record<string, string | number | undefined> {
  const state = filters.state.trim();
  const role = filters.role.trim();
  const industry = filters.industry.trim();
  const tag = filters.tag.trim();
  const bands: Record<string, string> = {
    high: "high", warm: "warm", standard: "standard",
    medium: "warm", low: "standard",
  };
  const intentBand = filters.intentBand === "" ? undefined : bands[filters.intentBand];
  if (filters.intentBand !== "" && intentBand === undefined) {
    throw new Error("Unknown HIGH-INTENT search band");
  }
  return {
    ...(state === "" ? {} : { state }),
    ...(role === "" ? {} : { role }),
    ...(industry === "" ? {} : { industry }),
    ...(tag === "" ? {} : { tag }),
    ...(intentBand === undefined ? {} : { intent_band: intentBand }),
    ...(filters.campaignId === "" ? {} : { campaign_id: filters.campaignId }),
    limit: LEADS_PAGE_LIMIT,
    ...(cursor === undefined ? {} : { cursor }),
  };
}

export function useRadarCampaigns(): UseQueryResult<RadarCampaignsPage> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["radar", "campaigns", orgId],
    enabled: orgId !== null,
    queryFn: async () => parseCampaignsPage(await api.get<unknown>("/radar/campaigns")),
    staleTime: 30_000,
  });
}

/** One binding-scoped delivery-ledger page. The caller owns the opaque cursor stack. */
export function useRadarLedgerPage(cursor: string | undefined): UseQueryResult<RadarLedgerPage> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["radar", "ledger", orgId, cursor],
    enabled: orgId !== null,
    queryFn: async ({ signal }) => parseLedgerPage(await api.get<unknown>("/radar/ledger", {
      query: { limit: LEDGER_PAGE_LIMIT, ...(cursor === undefined ? {} : { cursor }) },
      signal,
    })),
  });
}

/**
 * One page of the pool-wide search, addressed by the seam's opaque cursor. Page navigation
 * is a cursor stack held by the screen: forward follows `next_cursor`, back re-requests a
 * remembered cursor, and a filter change resets both.
 */
export function useRadarLeadsPage(
  filters: RadarLeadFilters,
  cursor: string | undefined,
): UseQueryResult<RadarLeadsPage> {
  const api = useApiClient();
  const { orgId } = useSession();
  const query = useMemo(() => toRadarSearchQuery(filters, cursor), [filters, cursor]);

  return useQuery({
    queryKey: ["radar", "leads", orgId, query],
    enabled: orgId !== null,
    queryFn: async () => parseLeadsPage(await api.get<unknown>("/radar/search", { query })),
    placeholderData: keepPreviousData,
  });
}

export function useRadarCampaignRuns(campaignId: string): UseQueryResult<RadarRunsPage> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["radar", "runs", orgId, campaignId],
    enabled: orgId !== null && campaignId !== "",
    queryFn: async () =>
      parseRunsPage(await api.get<unknown>(`/radar/campaigns/${encodeURIComponent(campaignId)}/runs`)),
    staleTime: 15_000,
  });
}

export function useRadarCampaignStats(campaignId: string): UseQueryResult<RadarStats> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["radar", "stats", orgId, campaignId],
    enabled: orgId !== null && campaignId !== "",
    queryFn: async () =>
      parseStats(await api.get<unknown>(`/radar/campaigns/${encodeURIComponent(campaignId)}/stats`)),
    staleTime: 15_000,
  });
}
